import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dateInput", "reasonInput", "counter", "submitButton", "dateError", "fileList", "addFileButton", "fileError"]
  static values = {
    maxLength: { type: Number, default: 500 },
    eligibleDates: Array,
    maxFileSize: { type: Number, default: 5242880 }  // 5MB in bytes
  }

  connect() {
    this.updateCounter()

    if (this.hasDateInputTarget && this.hasEligibleDatesValue) {
      const dates = this.eligibleDatesValue.sort()
      this.dateInputTarget.min = dates[0]
      this.dateInputTarget.max = dates[dates.length - 1]

      this.dateInputTarget.addEventListener('input', () => this.validateEligibleDate())
      this.dateInputTarget.addEventListener('change', () => this.validateEligibleDate())
    }
  }

  validateEligibleDate() {
    if (!this.hasDateInputTarget || !this.hasEligibleDatesValue) return true

    const selectedDate = this.dateInputTarget.value

    if (!selectedDate) {
      this.clearDateError()
      return true
    }

    const isEligible = this.eligibleDatesValue.includes(selectedDate)

    if (!isEligible) {
      this.showDateError()
      return false
    }

    this.clearDateError()
    return true
  }

  showDateError() {
    if (!this.hasDateInputTarget) return

    this.dateInputTarget.classList.add('is-invalid')

    if (this.hasDateErrorTarget) {
      this.dateErrorTarget.style.display = 'block'
    }
  }

  clearDateError() {
    if (!this.hasDateInputTarget) return

    this.dateInputTarget.classList.remove('is-invalid')

    if (this.hasDateErrorTarget) {
      this.dateErrorTarget.style.display = 'none'
    }
  }

  updateCounter() {
    if (!this.hasReasonInputTarget || !this.hasCounterTarget) return

    const currentLength = this.reasonInputTarget.value.length
    const remaining = this.maxLengthValue - currentLength

    this.counterTarget.textContent = `${currentLength} / ${this.maxLengthValue} caracteres`

    if (remaining < 50) {
      this.counterTarget.classList.add('text-warning')
      this.counterTarget.classList.remove('text-muted')
    } else {
      this.counterTarget.classList.add('text-muted')
      this.counterTarget.classList.remove('text-warning')
    }

    if (currentLength > this.maxLengthValue) {
      this.reasonInputTarget.value = this.reasonInputTarget.value.substring(0, this.maxLengthValue)
      this.updateCounter()
    }
  }

  validateForm() {
    const errors = []

    if (this.hasDateInputTarget) {
      const dateValue = this.dateInputTarget.value
      if (!dateValue) {
        errors.push("Debes seleccionar una fecha de pago")
      } else {
        if (this.hasEligibleDatesValue && !this.eligibleDatesValue.includes(dateValue)) {
          const dates = this.eligibleDatesValue.sort()
          const minDate = this.formatDate(dates[0])
          const maxDate = this.formatDate(dates[dates.length - 1])
          errors.push(`Debes seleccionar una fecha entre el ${minDate} (martes) y el ${maxDate} (jueves) de la siguiente semana`)
        }

        const selectedDate = new Date(dateValue)
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        // TODO: restore this after testing phase
        // if (selectedDate < today) {
        //   errors.push("La fecha debe ser futura")
        // }
      }
    }

    if (this.hasReasonInputTarget) {
      const reason = this.reasonInputTarget.value.trim()
      if (!reason) {
        errors.push("Debes ingresar un motivo para la prórroga")
      }
    }

    return errors
  }

  formatDate(dateString) {
    const date = new Date(dateString)
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/Mexico_City'
    }
    return date.toLocaleDateString('es-MX', options)
  }

  confirmSubmit(event) {
    event.preventDefault()
    event.stopPropagation()

    const errors = this.validateForm()

    if (errors.length > 0) {
      alert("Por favor corrige los siguientes errores:\n\n" + errors.join("\n"))
      return false
    }

    this.disableSubmitButton()

    this.element.submit()
  }

  disableSubmitButton() {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = true

    const originalText = this.submitButtonTarget.textContent
    this.submitButtonTarget.dataset.originalText = originalText

    this.submitButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Enviando...'
  }

  enableSubmitButton() {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = false

    const originalText = this.submitButtonTarget.dataset.originalText
    if (originalText) {
      this.submitButtonTarget.textContent = originalText
    }
  }

  addFileInput(event) {
    event.preventDefault()
    
    if (!this.hasFileListTarget) return

    const fileId = `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const fileRow = document.createElement('div')
    fileRow.className = 'file-input-row'
    fileRow.dataset.fileId = fileId

    fileRow.innerHTML = `
      <div class="file-input-wrapper">
        <input type="file"
               name="payment_extension[evidence_files][]"
               class="file-input"
               accept="image/*,application/pdf"
               data-action="change->customers--payment-extension-form#validateFile"
               data-file-id="${fileId}">
        <div class="file-preview" data-file-preview="${fileId}" style="display: none;">
          <span class="file-preview-name"></span>
          <span class="file-preview-size"></span>
        </div>
      </div>
      <button type="button"
              class="btn-remove-file"
              data-action="click->customers--payment-extension-form#removeFileInput"
              data-file-id="${fileId}">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
        </svg>
      </button>
    `

    this.fileListTarget.appendChild(fileRow)
    this.clearFileError()
  }

  removeFileInput(event) {
    const fileId = event.currentTarget.dataset.fileId
    const fileRow = this.fileListTarget.querySelector(`[data-file-id="${fileId}"]`)

    if (fileRow) {
      fileRow.remove()
    }

    this.clearFileError()
  }

  validateFile(event) {
    const input = event.target
    const fileId = input.dataset.fileId
    const file = input.files[0]

    if (!file) {
      this.hideFilePreview(fileId)
      return
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']
    if (!validTypes.includes(file.type)) {
      this.showFileError(`El archivo "${file.name}" no es un formato válido. Solo se permiten imágenes y PDF.`)
      input.value = ''
      this.hideFilePreview(fileId)
      return
    }

    if (file.size > this.maxFileSizeValue) {
      this.showFileError(`El archivo "${file.name}" excede el tamaño máximo de 5MB.`)
      input.value = ''
      this.hideFilePreview(fileId)
      return
    }

    this.showFilePreview(fileId, file)
    this.clearFileError()
  }

  showFilePreview(fileId, file) {
    const preview = this.fileListTarget.querySelector(`[data-file-preview="${fileId}"]`)
    if (!preview) return

    const nameSpan = preview.querySelector('.file-preview-name')
    const sizeSpan = preview.querySelector('.file-preview-size')

    if (nameSpan) nameSpan.textContent = file.name
    if (sizeSpan) sizeSpan.textContent = this.formatFileSize(file.size)

    preview.style.display = 'flex'

    const input = this.fileListTarget.querySelector(`input[data-file-id="${fileId}"]`)
    if (input) input.style.display = 'none'
  }

  hideFilePreview(fileId) {
    const preview = this.fileListTarget.querySelector(`[data-file-preview="${fileId}"]`)
    if (preview) {
      preview.style.display = 'none'
    }

    const input = this.fileListTarget.querySelector(`input[data-file-id="${fileId}"]`)
    if (input) input.style.display = 'block'
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  showFileError(message) {
    if (!this.hasFileErrorTarget) return
    this.fileErrorTarget.textContent = message
    this.fileErrorTarget.style.display = 'block'
  }

  clearFileError() {
    if (!this.hasFileErrorTarget) return
    this.fileErrorTarget.textContent = ''
    this.fileErrorTarget.style.display = 'none'
  }

  disconnect() {
    this.enableSubmitButton()
  }
}
